import {
    Button,
    Card,
    Drawer,
    Form,
    Input,
    Modal,
    notification,
    Select,
    Skeleton,
    theme,
    Tag,
    Tooltip,
} from "antd";
import {
    SearchOutlined,
    AppstoreOutlined,
    HistoryOutlined,
    MailOutlined,
    LockOutlined,
    PoweroffOutlined,
    ReloadOutlined,
    ApiOutlined,
} from "@ant-design/icons";
import { useContext, useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { SettingContext } from "../../routes";
import HTTP from "../../services/httpService";
import { encrypt, formatDate, getUserRole } from "../../services/util";
import CustomHeader from "../common/Header";
import CustomTable from "../common/Table";
import KasaDevices from "./AssignDevice";
import "./HotelWidget.css";

const { Option } = Select;

const restartServices = [
    { key: "3", name: "Set Position" },
    { key: "4", name: "Get Key Status" },
    { key: "5", name: "Retain Request" },
    { key: "3", name: "Start Dispense Notify" },
];

const isCustomer = () =>
    getUserRole() === "Customer" || getUserRole() === "Customer Success";

// ── Reusable toggle button ───────────────────────────
function DeviceToggleBtn({ device, onClick }) {
    const { token } = theme.useToken();
    const isOn = device?.relay_state === 1;
    const exists = Boolean(device);

    return (
        <Button
            size="small"
            disabled={!exists}
            onClick={onClick}
            style={{
                minWidth: 52,
                fontWeight: 600,
                fontSize: 12,
                borderRadius: 7,
                border: "none",
                background: !exists
                    ? token.colorBgElevated
                    : isOn
                    ? "rgba(20,184,166,0.15)" /* colorSuccess tint */
                    : "rgba(244,63,94,0.15)" /* colorError tint   */,
                color: !exists
                    ? token.colorTextTertiary
                    : isOn
                    ? token.colorSuccess /* #14B8A6 */
                    : token.colorError /* #F43F5E */,
                boxShadow: exists
                    ? `0 0 0 1px ${
                          isOn ? token.colorSuccess : token.colorError
                      }44`
                    : "none",
            }}
        >
            {exists ? (isOn ? "On" : "Off") : "—"}
        </Button>
    );
}

// ── Restart service button ───────────────────────────
function RestartBtn({ label, datatype, hotel_id, kiosk_id, onClick }) {
    const { token } = theme.useToken();
    return (
        <Button
            size="small"
            datatype={datatype}
            hotel_id={hotel_id}
            kiosk_id={kiosk_id}
            onClick={onClick}
            disabled={isCustomer()}
            style={{
                borderRadius: 7,
                fontWeight: 600,
                fontSize: 12,
                background: `${token.colorPrimary}15`,
                border: `1px solid ${token.colorPrimary}44`,
                color: token.colorPrimary,
            }}
        >
            {label}
        </Button>
    );
}

function HotelWidgets() {
    const { token } = theme.useToken();
    const userRole = getUserRole();
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const { selectedDates } = useContext(SettingContext);
    const [start_date, end_date] = selectedDates;

    const [buttonStatus, setButtonStatus] = useState(false);
    const [assignDrawerVisibility, setAssignDrawerVisibility] = useState(false);
    const [searchKey, setSearchKey] = useState("");
    const [totalRecords, setTotalRecords] = useState(0);
    const [hotelData, setHotelData] = useState([]);
    const [visible, setVisible] = useState(false);
    const [formData, setFormData] = useState({
        staff_id,
        start_date: formatDate(start_date),
        end_date: formatDate(end_date),
        page: 0,
        limit: 10,
        search_by: searchKey,
    });

    // ── Queries ─────────────────────────────────────
    const {
        status: widgetStatus,
        data: widgetRes,
        refetch: getHotelWidgets,
    } = useQuery(
        "hotel-widgets",
        async () => await HTTP("/hotels/getHotelWidgets", { ...formData }),
        { enabled: false },
    );

    useEffect(() => {
        getHotelWidgets();
    }, []);
    useEffect(() => {
        getHotelWidgets();
    }, [formData]);

    useEffect(() => {
        if (widgetStatus === "success") {
            const { data: hotels, total } = widgetRes;
            setTotalRecords(total);
            setHotelData(hotels);
        }
    }, [widgetStatus, widgetRes]);

    useEffect(() => {
        const [s, e] = selectedDates;
        setFormData((p) => ({
            ...p,
            start_date: formatDate(s),
            end_date: formatDate(e),
        }));
    }, [selectedDates]);

    // ── Handlers ─────────────────────────────────────
    function handleTableChange(pagination, _, sorter) {
        const { current, pageSize } = pagination;
        const { order, field } = sorter;
        const others = { page: current - 1, limit: pageSize };
        if (order !== undefined) {
            others.sorting = order === "ascend" ? 1 : 2;
            others.sort_by = field;
        } else {
            delete formData.sorting;
            delete formData.sort_by;
        }
        setFormData({ ...formData, ...others });
    }

    function handleSearchInputChange(event) {
        const value = event.target.value;
        setSearchKey(value);

        if (!value) {
            setFormData((prev) => ({ ...prev, page: 0, search_by: "" }));
        }
    }

    function handleSearchInputSubmit() {
        setFormData((prev) => ({
            ...prev,
            page: 0,
            search_by: searchKey.trim(),
        }));
    }

    function handleTypeSelection(e) {
        if (e === "3#Set Position" || e === "3#Start Dispense Notify") {
            form.setFieldsValue({
                action_type: e === "3#Set Position" ? 1 : 2,
            });
        } else {
            form.setFieldsValue({ action_type: "" });
        }
    }

    function handleServiceRequestSubmit(fd) {
        const [type] = fd.type.split("#");
        if (
            fd.type === "3#Set Position" ||
            fd.type === "3#Start Dispense Notify"
        ) {
            fd.action_type = fd.type === "3#Set Position" ? 1 : 2;
        }
        fd.type = type;
        requestRestart(fd, 1);
    }

    function getRestartHistory(e) {
        navigate("/restart-history", {
            state: {
                hotel_id: e.currentTarget.getAttribute("hotel_id"),
                kiosk_id: e.currentTarget.getAttribute("kiosk_id"),
            },
        });
    }

    function requestRestart(e, fromDropDown) {
        const details = fromDropDown
            ? e
            : {
                  type: e.currentTarget.getAttribute("datatype"),
                  hotel_id: e.currentTarget.getAttribute("hotel_id"),
                  kiosk_id: e.currentTarget.getAttribute("kiosk_id"),
              };
        form.setFieldsValue(details);
        setVisible(true);
    }

    function toggleKioskStatus(device_id, hotel_id, kiosk_id, is_for, type) {
        form.setFieldsValue({ device_id, hotel_id, kiosk_id, is_for, type });
        setVisible(true);
    }

    function closeModel() {
        setVisible(false);
        form.resetFields();
    }

    function toggleAssignDrawer() {
        setAssignDrawerVisibility((v) => !v);
        if (assignDrawerVisibility) getHotelWidgets();
    }

    async function handleSubmit(fd) {
        fd.password = encrypt(fd.password);
        let api_success, api_message;
        if (fd.is_for === undefined) {
            const [type] = fd.type.split("#");
            fd.type = type;
            setButtonStatus(true);
            if (type != 3) delete fd.action_type;
            const { success, message } = await HTTP(
                "/hotels/serviceRestartRequest",
                { staff_id, ...fd },
            );
            api_success = success;
            api_message = message;
        } else {
            const { success, message } = await HTTP(
                "/hotels/smartDeviceRestartRequest",
                { staff_id, ...fd },
            );
            getHotelWidgets();
            api_success = success;
            api_message = message;
        }
        notification[api_success === 0 ? "error" : "info"]({
            message: api_message,
        });
        setButtonStatus(false);
        closeModel();
    }

    // ── Column definitions ────────────────────────────
    const column = [
        {
            title: "Hotel",
            key: "hotel_name",
            fixed: "left",
            width: 200,
            sorter: () => {},
            render: (_, record) => (
                <div
                    onClick={() =>
                        navigate(
                            `/hotel-dashboard?id=${encrypt(
                                record.hotel_id.toString(),
                            ).replace("+", "plus")}`,
                        )
                    }
                    style={{ cursor: "pointer" }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 9,
                        }}
                    >
                        <div
                            style={{
                                width: 32,
                                height: 32,
                                borderRadius: 9,
                                background: `${token.colorPrimary}15`,
                                border: `1.5px solid ${token.colorPrimary}33`,
                                color: token.colorPrimary,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: 700,
                                fontSize: 12,
                                flexShrink: 0,
                            }}
                        >
                            {record.hotel_name?.[0]?.toUpperCase() || "H"}
                        </div>
                        <div>
                            <div
                                style={{
                                    color: token.colorPrimary,
                                    fontWeight: 600,
                                    fontSize: 13,
                                }}
                            >
                                {record.hotel_name}
                            </div>
                            <div
                                style={{
                                    color: token.colorTextTertiary,
                                    fontSize: 11,
                                }}
                            >
                                {record.city}
                            </div>
                        </div>
                    </div>
                </div>
            ),
        },
        {
            title: "Kiosk",
            dataIndex: "kiosk_name",
            key: "kiosk_name",
            sorter: () => {},
            width: 130,
            render: (name) => (
                <span
                    style={{
                        display: "inline-block",
                        background: token.colorBgElevated,
                        border: `1px solid ${token.colorBorder}`,
                        borderRadius: 6,
                        padding: "2px 9px",
                        fontFamily: "monospace",
                        fontSize: 12,
                        color: token.colorTextSecondary,
                    }}
                >
                    {name}
                </span>
            ),
        },
        {
            title: "Agent",
            width: 130,
            render: (_, r) => (
                <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <div
                        style={{
                            width: 26,
                            height: 26,
                            borderRadius: 7,
                            background: `${token.colorSuccess}15`,
                            border: `1px solid ${token.colorSuccess}33`,
                            color: token.colorSuccess,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 700,
                            fontSize: 11,
                        }}
                    >
                        {r.staff_first_name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <span
                        style={{
                            color: token.colorTextSecondary,
                            fontSize: 13,
                        }}
                    >
                        {r.staff_first_name} {r.staff_last_name}
                    </span>
                </div>
            ),
        },
        {
            title: "Call Status",
            width: 110,
            render: (_, record) => {
                const live = record.has_ongoing_call;
                return (
                    <span
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            background: live
                                ? "rgba(20,184,166,0.1)"
                                : "rgba(244,63,94,0.1)",
                            border: `1px solid ${
                                live ? token.colorSuccess : token.colorError
                            }44`,
                            borderRadius: 6,
                            padding: "2px 9px",
                            fontWeight: 700,
                            fontSize: 11,
                            color: live ? token.colorSuccess : token.colorError,
                        }}
                    >
                        <span
                            style={{
                                width: 5,
                                height: 5,
                                borderRadius: "50%",
                                background: "currentColor",
                                flexShrink: 0,
                                animation: live
                                    ? "widget-pulse 1.5s ease-in-out infinite"
                                    : "none",
                            }}
                        />
                        {live ? "LIVE" : "NO CALL"}
                    </span>
                );
            },
        },
        {
            title: "Smart Plug",
            width: 90,
            render: (_, record) => {
                const device = record?.devices?.find((d) => d.type == 1);
                return (
                    <DeviceToggleBtn
                        device={device}
                        onClick={() =>
                            toggleKioskStatus(
                                device?.device_id,
                                record.hotel_id,
                                record.kiosk_id,
                                1,
                                device?.relay_state == 1 ? 2 : 1,
                            )
                        }
                    />
                );
            },
        },
        {
            title: "Smart CC",
            width: 90,
            render: (_, record) => {
                const device = record?.devices?.find((d) => d.type == 2);
                return (
                    <DeviceToggleBtn
                        device={device}
                        onClick={() =>
                            toggleKioskStatus(
                                device?.device_id,
                                record.hotel_id,
                                record.kiosk_id,
                                2,
                                device?.relay_state == 1 ? 2 : 1,
                            )
                        }
                    />
                );
            },
        },
        {
            title: "HIM",
            width: 70,
            render: (_, record) => (
                <RestartBtn
                    label="HIM"
                    datatype={1}
                    hotel_id={record.hotel_id}
                    kiosk_id={record.kiosk_id}
                    onClick={requestRestart}
                />
            ),
        },
        {
            title: "ASM",
            width: 70,
            render: (_, record) => (
                <RestartBtn
                    label="ASM"
                    datatype={2}
                    hotel_id={record.hotel_id}
                    kiosk_id={record.kiosk_id}
                    onClick={requestRestart}
                />
            ),
        },
        {
            title: "Actions",
            width: 240,
            render: (_, record) => (
                <Form
                    style={{
                        display: "flex",
                        gap: 6,
                        alignItems: "flex-start",
                    }}
                    onFinish={handleServiceRequestSubmit}
                    initialValues={{
                        hotel_id: record.hotel_id,
                        kiosk_id: record.kiosk_id,
                    }}
                >
                    <Form.Item
                        name="type"
                        rules={[{ required: true, message: "Select service" }]}
                        valuePropName="defaultValue"
                        style={{ margin: 0, flex: 1 }}
                    >
                        <Select
                            style={{ width: "100%" }}
                            onChange={handleTypeSelection}
                            placeholder="Service"
                        >
                            {restartServices.map((s) => (
                                <Option
                                    key={`${s.key}#${s.name}`}
                                    value={`${s.key}#${s.name}`}
                                >
                                    {s.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="hotel_id" style={{ display: "none" }}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="action_type" style={{ display: "none" }}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="kiosk_id" style={{ display: "none" }}>
                        <Input />
                    </Form.Item>
                    <Form.Item style={{ margin: 0 }}>
                        <Button
                            type="primary"
                            size="small"
                            htmlType="submit"
                            disabled={isCustomer()}
                            style={{ borderRadius: 7, fontWeight: 600 }}
                        >
                            Run
                        </Button>
                    </Form.Item>
                </Form>
            ),
        },
        {
            title: "History",
            width: 90,
            render: (_, record) => (
                <Tooltip title="Get Restart History">
                    <Button
                        size="small"
                        icon={<HistoryOutlined />}
                        hotel_id={record.hotel_id}
                        kiosk_id={record.kiosk_id}
                        onClick={getRestartHistory}
                        style={{
                            borderRadius: 7,
                            background: `${token.colorWarning}15`,
                            border: `1px solid ${token.colorWarning}44`,
                            color: token.colorWarning,
                            fontWeight: 600,
                            fontSize: 12,
                        }}
                    >
                        Logs
                    </Button>
                </Tooltip>
            ),
        },
        {
            title: "Support",
            fixed: "right",
            width: 80,
            render: (_, record) => {
                const hasTicket = record.has_sp_open_ticket;
                return (
                    <Tooltip
                        title={
                            hasTicket
                                ? "Open support ticket — click to view"
                                : "No open tickets"
                        }
                    >
                        <div
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 28,
                                height: 28,
                                borderRadius: 8,
                                background: hasTicket
                                    ? "rgba(244,63,94,0.12)"
                                    : "rgba(20,184,166,0.12)",
                                border: `1px solid ${
                                    hasTicket
                                        ? token.colorError
                                        : token.colorSuccess
                                }44`,
                                cursor: hasTicket ? "pointer" : "default",
                            }}
                            onClick={() => {
                                if (hasTicket) {
                                    navigate("/support-details-table", {
                                        state: {
                                            id: record.hotel_id,
                                            filter: false,
                                        },
                                    });
                                }
                            }}
                        >
                            <span
                                style={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    background: hasTicket
                                        ? token.colorError
                                        : token.colorSuccess,
                                    animation: hasTicket
                                        ? "widget-pulse 1.8s ease-in-out infinite"
                                        : "none",
                                }}
                            />
                        </div>
                    </Tooltip>
                );
            },
        },
    ];

    const isReady = widgetStatus !== "loading" && widgetStatus !== "idle";

    return (
        <>
            <Card className="dashboard-container">
                <CustomHeader />

                {/* ── Page header ── */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        margin: "16px 0 20px",
                    }}
                >
                    <div>
                        <h2
                            style={{
                                fontSize: 22,
                                fontWeight: 700,
                                color: token.colorText,
                                margin: 0,
                                letterSpacing: "-0.01em",
                            }}
                        >
                            Live Hotel Dashboard
                        </h2>
                        <p
                            style={{
                                fontSize: 13,
                                color: token.colorTextTertiary,
                                margin: "4px 0 0",
                            }}
                        >
                            Monitor kiosk status, devices and active calls in
                            real time
                        </p>
                    </div>

                    <div
                        style={{
                            display: "flex",
                            gap: 10,
                            alignItems: "center",
                        }}
                    >
                        {/* Count pill */}
                        {isReady && (
                            <div
                                style={{
                                    background: `${token.colorPrimary}15`,
                                    border: `1px solid ${token.colorPrimary}33`,
                                    borderRadius: token.borderRadius,
                                    padding: "8px 18px",
                                    textAlign: "center",
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: 20,
                                        fontWeight: 700,
                                        color: token.colorPrimary,
                                    }}
                                >
                                    {totalRecords}
                                </div>
                                <div
                                    style={{
                                        fontSize: 11,
                                        color: token.colorTextTertiary,
                                    }}
                                >
                                    Kiosks
                                </div>
                            </div>
                        )}

                        {(userRole === "Super Admin" ||
                            userRole === "Admin" ||
                            userRole === "Support") && (
                            <Button
                                type="primary"
                                icon={<ApiOutlined />}
                                onClick={toggleAssignDrawer}
                                style={{
                                    borderRadius: 9,
                                    height: 38,
                                    fontWeight: 600,
                                }}
                            >
                                Kasa Devices
                            </Button>
                        )}
                    </div>
                </div>

                {/* ── Search + table section ── */}
                <div
                    style={{
                        background: token.colorBgElevated,
                        border: `1px solid ${token.colorBorderSecondary}`,
                        borderRadius: token.borderRadius,
                        padding: "20px",
                    }}
                >
                    {/* Search bar */}
                    <div
                        style={{
                            display: "flex",
                            gap: 10,
                            marginBottom: 16,
                            background: token.colorBgContainer,
                            border: `1px solid ${token.colorBorder}`,
                            borderRadius: token.borderRadius,
                            padding: "10px 16px",
                        }}
                    >
                        <Input
                            prefix={
                                <SearchOutlined
                                    style={{ color: token.colorTextTertiary }}
                                />
                            }
                            placeholder="Search by hotel name or kiosk…"
                            value={searchKey}
                            onChange={handleSearchInputChange}
                            onPressEnter={handleSearchInputSubmit}
                            allowClear
                            style={{ flex: 1, maxWidth: 340 }}
                        />
                        <Button
                            type="primary"
                            onClick={handleSearchInputSubmit}
                            style={{ borderRadius: 8, fontWeight: 600 }}
                        >
                            Search
                        </Button>
                    </div>

                    {/* Table */}
                    {isReady ? (
                        <CustomTable
                            columns={column}
                            tableData={hotelData}
                            noOfrecords={totalRecords}
                            handleTableChange={handleTableChange}
                            className="dashboard-table hotel-widget-table"
                            rowKey="kiosk_id"
                            isScroll
                        />
                    ) : (
                        <Skeleton
                            active
                            title={false}
                            paragraph={{ rows: 10, width: "100%" }}
                        />
                    )}
                </div>
            </Card>

            {/* ── Auth / Action Modal ── */}
            <Modal
                title={
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                        }}
                    >
                        <div
                            style={{
                                width: 3,
                                height: 18,
                                borderRadius: 2,
                                background: `linear-gradient(180deg,${token.colorPrimary},${token.colorSuccess})`,
                            }}
                        />
                        <LockOutlined style={{ color: token.colorPrimary }} />
                        <span
                            style={{
                                color: token.colorText,
                                fontWeight: 600,
                                fontSize: 16,
                            }}
                        >
                            Confirm Action
                        </span>
                    </div>
                }
                open={visible}
                onCancel={closeModel}
                destroyOnHidden
                footer={null}
                styles={{
                    content: {
                        background: token.colorBgContainer,
                        border: `1px solid ${token.colorBorderSecondary}`,
                        borderRadius: 12,
                        padding: 28,
                    },
                    header: {
                        background: token.colorBgContainer,
                        borderBottom: `1px solid ${token.colorBorderSecondary}`,
                        paddingBottom: 16,
                    },
                    mask: { backdropFilter: "blur(4px)" },
                }}
            >
                <p
                    style={{
                        fontSize: 13,
                        color: token.colorTextTertiary,
                        marginBottom: 20,
                    }}
                >
                    Please verify your credentials to perform this action.
                </p>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    autoComplete="off"
                >
                    <Form.Item
                        label={
                            <span
                                style={{
                                    color: token.colorTextSecondary,
                                    fontSize: 12,
                                }}
                            >
                                Username
                            </span>
                        }
                        name="email"
                        rules={[
                            {
                                required: true,
                                message: "Please input username.",
                            },
                        ]}
                    >
                        <Input
                            prefix={
                                <MailOutlined
                                    style={{ color: token.colorTextTertiary }}
                                />
                            }
                            placeholder="user@company.com"
                        />
                    </Form.Item>

                    <Form.Item
                        label={
                            <span
                                style={{
                                    color: token.colorTextSecondary,
                                    fontSize: 12,
                                }}
                            >
                                Password
                            </span>
                        }
                        name="password"
                        rules={[
                            {
                                required: true,
                                message: "Please enter password.",
                            },
                        ]}
                    >
                        <Input.Password
                            prefix={
                                <LockOutlined
                                    style={{ color: token.colorTextTertiary }}
                                />
                            }
                            placeholder="Password"
                        />
                    </Form.Item>

                    {/* Hidden fields */}
                    {[
                        "action_type",
                        "type",
                        "hotel_id",
                        "kiosk_id",
                        "device_id",
                        "is_for",
                    ].map((name) => (
                        <Form.Item
                            key={name}
                            name={name}
                            style={{ display: "none" }}
                        >
                            <Input disabled />
                        </Form.Item>
                    ))}

                    <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                        <Button
                            onClick={closeModel}
                            style={{
                                flex: 1,
                                height: 40,
                                borderRadius: 9,
                                background: token.colorBgElevated,
                                border: `1px solid ${token.colorBorder}`,
                                color: token.colorTextSecondary,
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={buttonStatus}
                            style={{
                                flex: 2,
                                height: 40,
                                borderRadius: 9,
                                fontWeight: 600,
                            }}
                        >
                            Confirm
                        </Button>
                    </div>
                </Form>
            </Modal>

            {/* ── Kasa Devices Drawer ── */}
            <Drawer
                title={
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                        }}
                    >
                        <ApiOutlined style={{ color: token.colorPrimary }} />
                        <span
                            style={{ color: token.colorText, fontWeight: 600 }}
                        >
                            Kasa Devices
                        </span>
                    </div>
                }
                open={assignDrawerVisibility}
                onClose={toggleAssignDrawer}
                destroyOnHidden
                width="75vw"
                styles={{
                    body: { padding: 24, background: token.colorBgContainer },
                    header: {
                        background: token.colorBgElevated,
                        borderBottom: `1px solid ${token.colorBorderSecondary}`,
                    },
                }}
            >
                <KasaDevices />
            </Drawer>
        </>
    );
}

export { HotelWidgets };
